import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { NetflixPlayer, CommentsDrawer, ShareModal } from "../shared";
import { savePost } from "../../../functions/post";
import "./style.css";
import axios from "axios";

export default function NetflixFeed() {
  const { user } = useSelector((state) => ({ ...state }));

  const readFromStorage = (key, fallback) => {
    if (typeof window === "undefined") return fallback;
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      console.warn("Unable to read storage", err);
      return fallback;
    }
  };

  const progressKey = `netflixProgress_${user?.id || "guest"}`;
  const myListKey = `netflixMyList_${user?.id || "guest"}`;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContent, setSelectedContent] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeMaturity, setActiveMaturity] = useState("all");
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [playMode, setPlayMode] = useState("main");
  const [showComments, setShowComments] = useState(null);
  const [showShare, setShowShare] = useState(null);
  const [resumeTime, setResumeTime] = useState(0);
  const [showDetails, setShowDetails] = useState(null);
  const [progressMap, setProgressMap] = useState(() => readFromStorage(progressKey, {}));
  const [myList, setMyList] = useState(() => readFromStorage(myListKey, []));
  const videoRef = useRef(null);

  useEffect(() => {
    setProgressMap(readFromStorage(progressKey, {}));
    setMyList(readFromStorage(myListKey, []));
  }, [progressKey, myListKey]);

  useEffect(() => {
    localStorage.setItem(progressKey, JSON.stringify(progressMap));
  }, [progressKey, progressMap]);

  useEffect(() => {
    localStorage.setItem(myListKey, JSON.stringify(myList));
  }, [myListKey, myList]);

  const categories = useMemo(() => ([
    { id: "all", name: "Todos" },
    { id: "movie", name: "Filmes", contentType: "movie" },
    { id: "series", name: "Séries", contentType: "series" },
    { id: "documentary", name: "Documentários", contentType: "documentary" },
    { id: "short", name: "Curtas", contentType: "short" },
    { id: "animation", name: "Animação", genre: "animation" },
  ]), []);

  const maturityFilters = useMemo(() => ([
    { id: "all", name: "Todas" },
    { id: "L", name: "Livre" },
    { id: "10", name: "10" },
    { id: "12", name: "12" },
    { id: "14", name: "14" },
    { id: "16", name: "16" },
    { id: "18", name: "18" },
  ]), []);

  const genres = useMemo(() => ([
    { id: "action", name: "Ação" },
    { id: "comedy", name: "Comédia" },
    { id: "drama", name: "Drama" },
    { id: "horror", name: "Terror" },
    { id: "romance", name: "Romance" },
    { id: "scifi", name: "Ficção Científica" },
    { id: "thriller", name: "Suspense" },
    { id: "documentary", name: "Documentário" },
  ]), []);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("limit", "50");
      const selectedCategory = categories.find((c) => c.id === activeCategory);
      if (selectedCategory?.contentType) {
        params.set("contentType", selectedCategory.contentType);
      }
      if (selectedCategory?.genre) {
        params.set("genre", selectedCategory.genre);
      }
      if (activeMaturity !== "all") {
        params.set("maturityRating", activeMaturity);
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/netflix?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setPosts(data.posts || []);
    } catch (error) {
      console.error("Error fetching Netflix feed:", error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, activeMaturity, user.token, categories]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Auto-rotate featured content
  useEffect(() => {
    if (posts.length > 0) {
      const interval = setInterval(() => {
        setFeaturedIndex((prev) => (prev + 1) % Math.min(posts.length, 5));
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [posts.length]);

  // Group posts by genre
  const postsByGenre = genres.reduce((acc, genre) => {
    const genrePosts = posts.filter((post) => {
      const postGenre = post.netflixData?.genre;
      if (Array.isArray(postGenre)) {
        return postGenre.includes(genre.id);
      }
      return postGenre === genre.id;
    });
    if (genrePosts.length > 0) {
      acc[genre.id] = { name: genre.name, posts: genrePosts };
    }
    return acc;
  }, {});

  // Featured post
  const featuredPosts = posts.slice(0, 5);
  const featured = featuredPosts[featuredIndex];
  const myListPosts = posts.filter((p) => myList.includes(p._id));

  const getMaturityBadge = (rating) => {
    const badges = {
      L: { color: "#46d369", text: "L" },
      10: { color: "#46d369", text: "10" },
      12: { color: "#f9c034", text: "12" },
      14: { color: "#f9c034", text: "14" },
      16: { color: "#d83535", text: "16" },
      18: { color: "#d83535", text: "18" },
    };
    return badges[rating] || badges["L"];
  };

  const getYear = (item) => item?.netflixData?.releaseYear || item?.netflixData?.year;

  const getDurationLabel = (duration) => {
    if (!duration || Number.isNaN(duration)) return null;
    const hours = Math.floor(duration / 60);
    const minutes = Math.round(duration % 60);
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, "0")}min`;
    }
    return `${minutes} min`;
  };

  const getGenresLabel = (post) => {
    const genreValue = post?.netflixData?.genre;
    if (Array.isArray(genreValue)) {
      return genreValue
        .map((g) => genres.find((item) => item.id === g)?.name)
        .filter(Boolean)
        .join(", ");
    }
    return genres.find((g) => g.id === genreValue)?.name;
  };

  const continueWatching = posts
    .map((post) => ({ post, progress: progressMap[post._id] }))
    .filter(({ progress }) =>
      progress && progress.duration && progress.currentTime > 20 && progress.currentTime < progress.duration - 15
    )
    .sort((a, b) => (b.progress?.updatedAt || 0) - (a.progress?.updatedAt || 0))
    .map(({ post }) => post);

  const isNew = (post) => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Date(post.createdAt).getTime() > sevenDaysAgo;
  };

  const newReleases = posts.filter(isNew).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const topTen = posts
    .sort((a, b) => (b.reactions?.length || 0) + (b.shares?.length || 0) - (a.reactions?.length || 0) - (a.shares?.length || 0))
    .slice(0, 10);

  const filteredBySearch = searchQuery.trim()
    ? posts.filter((p) =>
        p.netflixData?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.netflixData?.synopsis?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.netflixData?.cast?.some((c) => c.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : posts;

  const handlePlayContent = (post) => {
    setSelectedContent(post);
    setResumeTime(progressMap[post._id]?.currentTime || 0);
    setShowPlayer(true);
  };

  const handleClosePlayer = () => {
    setShowPlayer(false);
    setSelectedContent(null);
  };

  const handleProgressUpdate = ({ postId, currentTime, duration }) => {
    if (!postId) return;
    setProgressMap((prev) => ({
      ...prev,
      [postId]: {
        currentTime,
        duration,
        updatedAt: Date.now(),
      },
    }));
  };

  const handleAutoPlayNext = (finishedPost) => {
    const currentIndex = posts.findIndex((p) => p._id === finishedPost?._id);
    if (currentIndex !== -1 && currentIndex < posts.length - 1) {
      const nextPost = posts[currentIndex + 1];
      setSelectedContent(nextPost);
      setResumeTime(progressMap[nextPost._id]?.currentTime || 0);
      setShowPlayer(true);
    } else {
      setShowPlayer(false);
    }
  };

  const toggleMyList = async (postId) => {
    setMyList((prev) => {
      const exists = prev.includes(postId);
      return exists ? prev.filter((id) => id !== postId) : [...prev, postId];
    });

    try {
      await savePost(postId, user.token);
    } catch (error) {
      console.error("Error toggling My List:", error);
    }
  };

  const inMyList = (postId) => myList.includes(postId);

  const openComments = (post) => setShowComments(post);
  const openShare = (post) => setShowShare(post);
  const handleShareAction = () => setShowShare(null);


  // Calculate user rating for a post
  const getUserRating = (post) => {
    const reactionsCount = post.reactions?.length || 0;
    const commentsCount = post.comments?.length || 0;
    const sharesCount = post.shares?.length || 0;
    const totalEngagement = reactionsCount + (commentsCount * 1.5) + (sharesCount * 2);
    return Math.min(100, Math.round(totalEngagement / Math.max(1, posts.length) * 10));
  };

  // Get audience choice (high rated + high engagement)
  const audienceChoice = posts
    .filter(p => getUserRating(p) >= 70)
    .sort((a, b) => getUserRating(b) - getUserRating(a))
    .slice(0, 15);

  // Get recommendations based on selected content or My List preferences
  const getRecommendations = () => {
    if (myListPosts.length === 0) return [];
    
    const genresInList = new Set();
    myListPosts.forEach(p => {
      const genres = p.netflixData?.genre;
      if (Array.isArray(genres)) {
        genres.forEach(g => genresInList.add(g));
      } else if (genres) {
        genresInList.add(genres);
      }
    });

    return posts
      .filter(p => !myList.includes(p._id))
      .filter(p => {
        const postGenres = p.netflixData?.genre;
        const postGenreArray = Array.isArray(postGenres) ? postGenres : (postGenres ? [postGenres] : []);
        return postGenreArray.some(g => genresInList.has(g));
      })
      .sort((a, b) => getUserRating(b) - getUserRating(a))
      .slice(0, 12);
  };

  const recommendations = getRecommendations();

  if (loading) {
    return (
      <div className="netflix_feed_loading">
        <div className="netflix_loader"></div>
      </div>
    );
  }

  return (
    <div className="netflix_feed">
      {/* Featured Hero */}
      {featured && (
        <div className="netflix_hero">
          <div className="netflix_hero_bg">
            {featured.videos?.[0] ? (
              <video
                ref={videoRef}
                src={featured.videos[0].url || featured.videos[0]}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={
                  featured.netflixData?.thumbnail ||
                  featured.images?.[0]?.url ||
                  featured.images?.[0]
                }
                alt=""
              />
            )}
            <div className="netflix_hero_gradient" />
          </div>
          <div className="netflix_hero_content">
            <div className="netflix_content_type">
              {featured.netflixData?.contentType === "series" ? "S É R I E" : featured.netflixData?.contentType === "documentary" ? "D O C" : "F I L M E"}
            </div>
            <h1 className="netflix_hero_title">
              {featured.netflixData?.title || featured.text}
            </h1>
            <div className="netflix_hero_meta">
              {featured.netflixData?.maturityRating && (
                <span
                  className="netflix_maturity"
                  style={{
                    borderColor: getMaturityBadge(featured.netflixData.maturityRating).color,
                  }}
                >
                  {getMaturityBadge(featured.netflixData.maturityRating).text}
                </span>
              )}
              {typeof featured.netflixData?.matchPercentage === "number" && (
                <span>{Math.round(featured.netflixData.matchPercentage)}% de compatibilidade</span>
              )}
              {getYear(featured) && <span>{getYear(featured)}</span>}
              {getDurationLabel(featured.netflixData?.duration) && (
                <span>{getDurationLabel(featured.netflixData.duration)}</span>
              )}
              {featured.netflixData?.season && (
                <span>{featured.netflixData.season} Temporadas</span>
              )}
            </div>
            <p className="netflix_hero_synopsis">
              {featured.netflixData?.synopsis || featured.text}
            </p>
            <div className="netflix_hero_actions">
              <button
                className="netflix_play_btn"
                onClick={() => {
                  setPlayMode("main");
                  handlePlayContent(featured);
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Assistir
              </button>
              {featured.netflixData?.trailerUrl && (
                <button
                  className="netflix_info_btn"
                  onClick={() => {
                    setPlayMode("trailer");
                    handlePlayContent(featured);
                  }}
                  title="Assistir trailer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Trailer
                </button>
              )}
              <button className="netflix_info_btn" onClick={() => openComments(featured)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
                Comentários
              </button>
              <button
                className="netflix_info_btn"
                onClick={() => openShare(featured)}
                title="Compartilhar"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.02-4.11c.53.5 1.23.81 2.01.81A2.99 2.99 0 0 0 20.99 5 2.99 2.99 0 0 0 18 2a2.99 2.99 0 0 0-2.99 3c0 .24.04.47.09.7L8.08 9.81A2.99 2.99 0 0 0 5 8a2.99 2.99 0 0 0-2.99 3A2.99 2.99 0 0 0 5 14c.79 0 1.5-.31 2.03-.82l6.99 4.11c-.05.21-.08.43-.08.65A2.99 2.99 0 0 0 18 21a2.99 2.99 0 0 0 2.99-3A2.99 2.99 0 0 0 18 16.08Z" />
                </svg>
                Compartilhar
              </button>
              <button
                className="netflix_info_btn"
                onClick={() => toggleMyList(featured._id)}
                title={inMyList(featured._id) ? "Remover da Minha Lista" : "Adicionar à Minha Lista"}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
                {inMyList(featured._id) ? "Na Minha Lista" : "Minha Lista"}
              </button>
            </div>
            <div className="netflix_hero_creator">
              <Link to={`/profile/${featured.user.username}`}>
                <img src={featured.user.picture} alt="" />
                <span>Por {featured.user.first_name} {featured.user.last_name}</span>
              </Link>
            </div>
          </div>
          {/* Hero Navigation Dots */}
          <div className="netflix_hero_dots">
            {featuredPosts.map((_, i) => (
              <button
                key={i}
                className={`netflix_hero_dot ${i === featuredIndex ? "active" : ""}`}
                onClick={() => setFeaturedIndex(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="netflix_search_container">
        <svg className="netflix_search_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="20" height="20">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          type="text"
          className="netflix_search_input"
          placeholder="Buscar filmes, séries, atores..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Categories */}
      <div className="netflix_categories">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`netflix_category_btn ${activeCategory === cat.id ? "active" : ""}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Maturity Filters */}
      <div className="netflix_maturity_filters">
        <span className="netflix_filter_label">Classificação:</span>
        <div className="netflix_filter_chips">
          {maturityFilters.map((filter) => (
            <button
              key={filter.id}
              className={`netflix_filter_chip ${activeMaturity === filter.id ? "active" : ""}`}
              onClick={() => setActiveMaturity(filter.id)}
            >
              {filter.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content Rows */}
      <div className="netflix_rows">
        {/* New Releases */}
        {!searchQuery && newReleases.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Novidades</h2>
            <div className="netflix_row_content">
              {newReleases.map((post) => (
                <div
                  key={post._id}
                  className="netflix_card"
                  onClick={() => handlePlayContent(post)}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={post.netflixData?.thumbnail || post.images[0]?.url || post.images[0]}
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    {isNew(post) && <span className="netflix_badge_new">NOVO</span>}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {typeof post.netflixData?.matchPercentage === "number" && (
                        <span className="netflix_match_badge">{Math.round(post.netflixData.matchPercentage)}%</span>
                      )}
                      {getGenresLabel(post) && <span>{getGenresLabel(post)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top 10 */}
        {!searchQuery && topTen.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Top 10</h2>
            <div className="netflix_row_content">
              {topTen.map((post, index) => (
                <div
                  key={post._id}
                  className="netflix_card netflix_card_top10"
                  onClick={() => handlePlayContent(post)}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img src={post.netflixData?.thumbnail || post.images[0]?.url || post.images[0]} alt="" />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_card_overlay">
                      <div className="netflix_top10_number">{index + 1}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchQuery && filteredBySearch.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Resultados da Busca</h2>
            <div className="netflix_row_content">
              {filteredBySearch.map((post) => (
                <div key={post._id} className="netflix_card" onClick={() => handlePlayContent(post)}>
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img src={post.netflixData?.thumbnail || post.images[0]?.url || post.images[0]} alt="" />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {post.netflixData?.maturityRating && (
                        <span className="netflix_maturity_small" style={{ borderColor: getMaturityBadge(post.netflixData.maturityRating).color }}>
                          {getMaturityBadge(post.netflixData.maturityRating).text}
                        </span>
                      )}
                      {getGenresLabel(post) && <span>{getGenresLabel(post)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State for Search */}
        {searchQuery && filteredBySearch.length === 0 && (
          <div className="netflix_empty">
            <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
              <path d="M15.5 1h-8C6.12 1 5 2.12 5 3.5v17C5 21.88 6.12 23 7.5 23h8c1.38 0 2.5-1.12 2.5-2.5v-17C18 2.12 16.88 1 15.5 1zm-4 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4H7V4h8v14z" />
            </svg>
            <h3>Nenhum resultado encontrado</h3>
            <p>Tente com outras palavras-chave</p>
          </div>
        )}
        {/* My List */}
        {myListPosts.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Minha Lista</h2>
            <div className="netflix_row_content">
              {myListPosts.map((post) => (
                <div
                  key={post._id}
                  className="netflix_card"
                  onClick={() => handlePlayContent(post)}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={
                          post.netflixData?.thumbnail ||
                          post.images[0]?.url ||
                          post.images[0]
                        }
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {post.netflixData?.maturityRating && (
                        <span
                          className="netflix_maturity_small"
                          style={{
                            borderColor: getMaturityBadge(post.netflixData.maturityRating).color,
                          }}
                        >
                          {getMaturityBadge(post.netflixData.maturityRating).text}
                        </span>
                      )}
                      {typeof post.netflixData?.matchPercentage === "number" && (
                        <span>{Math.round(post.netflixData.matchPercentage)}% match</span>
                      )}
                      {getGenresLabel(post) && (
                        <span>{getGenresLabel(post)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Continue Watching */}
        {continueWatching.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Continuar Assistindo</h2>
            <div className="netflix_row_content">
              {continueWatching.map((post) => {
                const progress = progressMap[post._id];
                const progressPercent = progress?.duration
                  ? Math.min(100, Math.round((progress.currentTime / progress.duration) * 100))
                  : 0;
                return (
                  <div
                    key={post._id}
                    className="netflix_card"
                    onClick={() => handlePlayContent(post)}
                  >
                    <div className="netflix_card_img">
                      {post.netflixData?.thumbnail || post.images?.[0] ? (
                        <img
                          src={
                            post.netflixData?.thumbnail ||
                            post.images[0]?.url ||
                            post.images[0]
                          }
                          alt=""
                        />
                      ) : (
                        <div className="netflix_card_placeholder">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                            <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                          </svg>
                        </div>
                      )}
                      <div className="netflix_play_icon">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <div className="netflix_continue_progress">
                        <div className="netflix_continue_bar" style={{ width: `${progressPercent}%` }} />
                      </div>
                    </div>
                    <div className="netflix_card_info">
                      <h4>{post.netflixData?.title || post.text}</h4>
                      <div className="netflix_card_meta">
                        {post.netflixData?.maturityRating && (
                          <span
                            className="netflix_maturity_small"
                            style={{
                              borderColor: getMaturityBadge(post.netflixData.maturityRating).color,
                            }}
                          >
                            {getMaturityBadge(post.netflixData.maturityRating).text}
                          </span>
                        )}
                        {post.netflixData?.genre && (
                          <span>{
                            Array.isArray(post.netflixData.genre)
                              ? post.netflixData.genre.map((g) => genres.find((item) => item.id === g)?.name).filter(Boolean).join(", ")
                              : genres.find((g) => g.id === post.netflixData.genre)?.name
                          }</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Trending */}
        {posts.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">Em Alta</h2>
            <div className="netflix_row_content">
              {posts.slice(0, 10).map((post, index) => (
                <div
                  key={post._id}
                  className="netflix_card"
                  onClick={() => handlePlayContent(post)}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={
                          post.netflixData?.thumbnail ||
                          post.images[0]?.url ||
                          post.images[0]
                        }
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_card_overlay">
                      <div className="netflix_card_number">{index + 1}</div>
                    </div>
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {post.netflixData?.maturityRating && (
                        <span
                          className="netflix_maturity_small"
                          style={{
                            borderColor: getMaturityBadge(post.netflixData.maturityRating).color,
                          }}
                        >
                          {getMaturityBadge(post.netflixData.maturityRating).text}
                        </span>
                      )}
                      {getGenresLabel(post) && (
                        <span>{getGenresLabel(post)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audience Choice */}
        {audienceChoice.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">
              <span>Escolha do Público</span>
            </h2>
            <div className="netflix_row_content">
              {audienceChoice.map((post) => (
                <div
                  key={post._id}
                  className="netflix_card"
                  onClick={() => handlePlayContent(post)}
                  title={post.netflixData?.synopsis || ""}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={
                          post.netflixData?.thumbnail ||
                          post.images[0]?.url ||
                          post.images[0]
                        }
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    {getUserRating(post) >= 90 && (
                      <span className="netflix_badge_trending">⭐ TOP</span>
                    )}
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {post.netflixData?.maturityRating && (
                        <span
                          className="netflix_maturity_small"
                          style={{
                            borderColor: getMaturityBadge(post.netflixData.maturityRating).color,
                          }}
                        >
                          {getMaturityBadge(post.netflixData.maturityRating).text}
                        </span>
                      )}
                      {getGenresLabel(post) && (
                        <span>{getGenresLabel(post)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended For You */}
        {recommendations.length > 0 && (
          <div className="netflix_row">
            <h2 className="netflix_row_title">
              <span>Recomendado para Você</span>
            </h2>
            <div className="netflix_row_content">
              {recommendations.map((post) => (
                <div
                  key={post._id}
                  className="netflix_card"
                  onClick={() => handlePlayContent(post)}
                  title={post.netflixData?.synopsis || ""}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={
                          post.netflixData?.thumbnail ||
                          post.images[0]?.url ||
                          post.images[0]
                        }
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="netflix_card_info">
                    <h4>{post.netflixData?.title || post.text}</h4>
                    <div className="netflix_card_meta">
                      {typeof post.netflixData?.matchPercentage === "number" && (
                        <span className="netflix_match_badge">{Math.round(post.netflixData.matchPercentage)}%</span>
                      )}
                      {post.netflixData?.maturityRating && (
                        <span
                          className="netflix_maturity_small"
                          style={{
                            borderColor: getMaturityBadge(post.netflixData.maturityRating).color,
                          }}
                        >
                          {getMaturityBadge(post.netflixData.maturityRating).text}
                        </span>
                      )}
                      {getGenresLabel(post) && (
                        <span>{getGenresLabel(post)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Genre Rows */}
        {Object.entries(postsByGenre).map(([genreId, { name, posts: genrePosts }]) => (
          <div key={genreId} className="netflix_row">
            <h2 className="netflix_row_title">{name}</h2>
            <div className="netflix_row_content">
              {genrePosts.map((post) => (
                <div
                  key={post._id}
                  className="netflix_card netflix_card_small"
                  onClick={() => handlePlayContent(post)}
                >
                  <div className="netflix_card_img">
                    {post.netflixData?.thumbnail || post.images?.[0] ? (
                      <img
                        src={
                          post.netflixData?.thumbnail ||
                          post.images[0]?.url ||
                          post.images[0]
                        }
                        alt=""
                      />
                    ) : (
                      <div className="netflix_card_placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    <div className="netflix_play_icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {posts.length === 0 && (
          <div className="netflix_empty">
            <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
            </svg>
            <h3>Nenhum conteúdo disponível</h3>
            <p>Seja o primeiro a compartilhar um filme ou série!</p>
          </div>
        )}
      </div>

      {/* Player Modal */}
      {showPlayer && selectedContent && (
        <div className="netflix_player_fullscreen">
          <NetflixPlayer
            content={selectedContent}
            onClose={handleClosePlayer}
            onProgress={handleProgressUpdate}
            onEnded={handleAutoPlayNext}
            startTime={resumeTime}
            autoPlay={true}
            preferTrailer={playMode === "trailer"}
          />
        </div>
      )}

      {/* Details Modal */}
      {showDetails && (
        <div className="netflix_details_modal" onClick={() => setShowDetails(null)}>
          <div className="netflix_details_content" onClick={(e) => e.stopPropagation()}>
            <button className="netflix_details_close" onClick={() => setShowDetails(null)}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
            
            {/* Background Image */}
            <div
              className="netflix_details_bg"
              style={{
                backgroundImage: `url(${showDetails.netflixData?.thumbnail || showDetails.images?.[0]?.url || showDetails.images?.[0]})`,
              }}
            />

            {/* Content */}
            <div className="netflix_details_body">
              <div className="netflix_details_poster">
                <img
                  src={showDetails.netflixData?.thumbnail || showDetails.images?.[0]?.url || showDetails.images?.[0]}
                  alt=""
                />
              </div>

              <div className="netflix_details_info">
                <h1 className="netflix_details_title">{showDetails.netflixData?.title || showDetails.text}</h1>

                {/* Meta Info */}
                <div className="netflix_details_meta">
                  {getYear(showDetails) && (
                    <span className="netflix_details_year">{getYear(showDetails)}</span>
                  )}
                  {showDetails.netflixData?.maturityRating && (
                    <span
                      className="netflix_maturity_badge_large"
                      style={{
                        borderColor: getMaturityBadge(showDetails.netflixData.maturityRating).color,
                        color: getMaturityBadge(showDetails.netflixData.maturityRating).color,
                      }}
                    >
                      {getMaturityBadge(showDetails.netflixData.maturityRating).text}
                    </span>
                  )}
                  {showDetails.netflixData?.duration && (
                    <span>{getDurationLabel(showDetails.netflixData.duration)}</span>
                  )}
                  {showDetails.netflixData?.episodeCount && (
                    <span>{showDetails.netflixData.episodeCount} episódios</span>
                  )}
                </div>

                {/* Actions */}
                <div className="netflix_details_actions">
                  <button
                    className="netflix_details_play_btn"
                    onClick={() => {
                      handlePlayContent(showDetails);
                      setShowDetails(null);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    Assistir
                  </button>
                  <button className="netflix_details_secondary_btn" onClick={() => toggleMyList(showDetails._id)}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                  </button>
                  <button className="netflix_details_secondary_btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.02-4.11c.53.5 1.23.81 2.01.81A2.99 2.99 0 0 0 20.99 5 2.99 2.99 0 0 0 18 2a2.99 2.99 0 0 0-2.99 3c0 .24.04.47.09.7L8.08 9.81A2.99 2.99 0 0 0 5 8a2.99 2.99 0 0 0-2.99 3A2.99 2.99 0 0 0 5 14c.79 0 1.5-.31 2.03-.82l6.99 4.11c-.05.21-.08.43-.08.65A2.99 2.99 0 0 0 18 21a2.99 2.99 0 0 0 2.99-3A2.99 2.99 0 0 0 18 16.08Z" />
                    </svg>
                  </button>
                </div>

                {/* Synopsis */}
                {showDetails.netflixData?.synopsis && (
                  <div className="netflix_details_section">
                    <h3>Sinopse</h3>
                    <p>{showDetails.netflixData.synopsis}</p>
                  </div>
                )}

                {/* Cast */}
                {showDetails.netflixData?.cast && showDetails.netflixData.cast.length > 0 && (
                  <div className="netflix_details_section">
                    <h3>Elenco</h3>
                    <div className="netflix_details_cast">
                      {showDetails.netflixData.cast.slice(0, 6).map((actor, idx) => (
                        <span key={idx}>{actor}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Genres */}
                {getGenresLabel(showDetails) && (
                  <div className="netflix_details_section">
                    <h3>Gêneros</h3>
                    <p>{getGenresLabel(showDetails)}</p>
                  </div>
                )}

                {/* Engagement Stats */}
                <div className="netflix_details_section">
                  <h3>Avaliação do Público</h3>
                  <div className="netflix_details_stats">
                    <div className="netflix_stat">
                      <span className="netflix_stat_value">{getUserRating(showDetails)}%</span>
                      <span className="netflix_stat_label">Relevância</span>
                    </div>
                    <div className="netflix_stat">
                      <span className="netflix_stat_value">{showDetails.reactions?.length || 0}</span>
                      <span className="netflix_stat_label">Reações</span>
                    </div>
                    <div className="netflix_stat">
                      <span className="netflix_stat_value">{showDetails.comments?.length || 0}</span>
                      <span className="netflix_stat_label">Comentários</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Drawer */}
      {showComments && (
        <CommentsDrawer post={showComments} user={user} onClose={() => setShowComments(null)} />
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          post={showShare}
          user={user}
          onClose={() => setShowShare(null)}
          onShare={handleShareAction}
        />
      )}

    </div>
  );
}
